import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import { Input } from "@/shared/ui/input";
import {
  FileText, ArrowLeft, ChevronRight, ChevronDown, FolderOpen,
  CheckCircle2, AlertTriangle, IndianRupee, Upload, Mail,
} from "lucide-react";
import { toast } from "sonner";

export default function ScopePOPortal() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/scope-po-board")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto" data-testid="scope-po-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} onBack={() => setCode(null)} onChanged={load} />
      ) : (
        <ProjectList projects={projects} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="scope-po-list">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center">
          <IndianRupee size={20} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Purchase Orders</h1>
          <p className="text-sm text-zinc-500">Raise POs for new variations & scope breaches — no Project BOQ needed.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl"
          data-testid="scope-po-empty">
          No POs pending. Everything is covered.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)}
              data-testid={`scope-po-project-${p.project_code}`}
              className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-amber-300 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                <ChevronRight size={16} className="text-zinc-300" />
              </div>
              <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.project_name}</div>
              <div className="mt-4 text-sm">
                {p.pending_count > 0 ? (
                  <span className="text-rose-700 font-medium">{p.pending_count} need action</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircle2 size={14} /> all done
                  </span>
                )}
                <span className="text-zinc-400"> · {p.total} item(s)</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectView({ project, onBack, onChanged }) {
  const totalPending = project.pending_count;
  return (
    <div data-testid="scope-po-view">
      <button onClick={onBack} data-testid="scope-po-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={project.project_code} /></span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.project_name}</h1>
        </div>
        {totalPending > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1.5 text-sm font-medium"
            data-testid="scope-po-pending-count">
            <span className="w-2 h-2 rounded-full bg-rose-500" /> {totalPending} item(s) need action
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
            <CheckCircle2 size={15} /> all caught up
          </span>
        )}
      </div>
      <div className="space-y-4">
        {(project.items || []).map((it) => (
          it.kind === "base_po"
            ? <BasePOCard key={it.boq_id} it={it} code={project.project_code} onChanged={onChanged} />
            : <POCard key={it.boq_id} it={it} code={project.project_code} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function POCard({ it, code, onChanged }) {
  const isVar = it.kind === "variation";
  const pending = isVar ? it.variation_status === "pending_po" : it.amended_po_status !== "approved";
  const poUploaded = isVar && it.variation_status === "po_uploaded";
  const emailUrl = isVar ? it.variation_email_url : it.amended_email_url;
  const emailConfirmed = pending && !!emailUrl;
  const [open, setOpen] = useState(pending || poUploaded);
  const iconCls = isVar ? "bg-violet-50 text-violet-600" : "bg-rose-50 text-rose-600";
  const chipCls = pending
    ? (emailConfirmed ? "bg-amber-100 text-amber-800" : (isVar ? "bg-violet-100 text-violet-800" : "bg-rose-100 text-rose-800"))
    : poUploaded ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700";

  const [url, setUrl] = useState("");
  const [poName, setPoName] = useState("");
  const [ref, setRef] = useState(isVar ? (it.variation_po_ref || "") : (it.amended_po_ref || ""));
  const [amended, setAmended] = useState(isVar ? !!it.variation_amended : false);
  const [rate, setRate] = useState(it.rate || "");
  const [qty, setQty] = useState(isVar ? (it.quantity_required || "") : (it.measured_total || it.approved_quantity || ""));
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const amount = (parseFloat(rate || "0") || 0) * (parseFloat(qty || "0") || 0);

  async function uploadVarPO(docType = "po") {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the PO file"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${it.boq_id}/variation-po`, {
        file_url: url, file_name: poName, po_ref: ref, amended, doc_type: docType,
        rate: parseFloat(rate || "0") || 0, total_quantity: parseFloat(qty || "0") || 0,
      });
      toast.success(docType === "email" ? "Email confirmation saved — awaiting the actual PO." : "PO + approved value saved.");
      setReplacing(false); setUrl("");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  async function approveVar() {
    setSaving(true);
    try { await api.post(`/projects/${code}/boq/${it.boq_id}/variation-approve`); toast.success("Variation approved."); onChanged(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  async function uploadAmendedPO(docType = "po") {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the amended PO file"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${it.boq_id}/amended-po`, {
        file_url: url, file_name: poName, po_ref: ref, doc_type: docType,
        rate: parseFloat(rate || "0") || 0, new_quantity: parseFloat(qty || "0") || 0,
      });
      toast.success(docType === "email" ? "Email confirmation saved — awaiting the actual PO." : "Amended PO saved — scope raised.");
      setUrl("");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  const submitPO = (docType) => (isVar ? uploadVarPO(docType) : uploadAmendedPO(docType));

  return (
    <div className={`rounded-2xl border bg-white shadow-sm overflow-hidden border-l-4 ${pending ? (emailConfirmed ? "border-l-amber-400" : "border-l-rose-500") : poUploaded ? "border-l-amber-400" : "border-l-emerald-400"} border-zinc-200`}
      data-testid={`scope-po-card-${it.boq_id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`scope-po-toggle-${it.boq_id}`}
        className="w-full px-5 py-3.5 flex items-center gap-3 text-left">
        <ChevronDown size={16} className={`text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconCls}`}>
          {isVar ? <FolderOpen size={17} /> : <AlertTriangle size={17} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 truncate">{it.material_name}</div>
          <div className="text-xs text-zinc-400 truncate">
            {isVar ? "New variation" : "Scope exceeded"}{it.unit ? ` · ${it.quantity_required || it.approved_quantity || ""} ${it.unit}` : ""}
          </div>
        </div>
        <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${chipCls}`}
          data-testid={`scope-po-status-${it.boq_id}`}>
          {pending ? (emailConfirmed ? "Email confirmed · awaiting PO" : (isVar ? "Needs fresh PO" : "Needs amended PO"))
            : poUploaded ? "Awaiting approval" : "PO uploaded"}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {it.description && <p className="text-sm text-zinc-600">{it.description}</p>}
          {!isVar && (
            <div className="text-xs text-zinc-500 flex gap-4">
              <span>Approved: <b className="text-zinc-700">{it.approved_quantity} {it.unit}</b></span>
              <span>Measured on site: <b className="text-rose-600">{it.measured_total} {it.unit}</b></span>
            </div>
          )}
          {/* interim email confirmation on file */}
          {emailConfirmed && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
              data-testid={`scope-po-email-banner-${it.boq_id}`}>
              <Mail size={13} />
              <span>Email confirmation on file — upload the actual PO when it arrives.</span>
              <a href={emailUrl} target="_blank" rel="noreferrer" className="underline ml-1">
                {(isVar ? it.variation_email_name : it.amended_email_name) || "View email"}
              </a>
            </div>
          )}
          {/* existing PO link */}
          {((isVar && it.variation_po_url) || (!isVar && it.amended_po_url)) && (
            <div className="flex items-center gap-1.5 text-xs text-zinc-600">
              <FileText size={13} />
              <a href={isVar ? it.variation_po_url : it.amended_po_url} target="_blank" rel="noreferrer" className="underline">
                {isVar ? (it.variation_amended ? "Amended PO" : "New PO") : "Amended PO"}
                {(isVar ? it.variation_po_ref : it.amended_po_ref) ? ` · ${isVar ? it.variation_po_ref : it.amended_po_ref}` : ""}
              </a>
            </div>
          )}

          {/* Upload form (pending, or replacing a wrong PO before approval) */}
          {(pending || replacing) && (
            <div className="rounded-xl border border-zinc-200 p-4 space-y-4">
              <FileUpload value={url} label="Attach PO / email confirmation"
                onChange={(x) => { setUrl(x); setPoName(x ? (x.split("/").pop() || "po") : ""); }}
                testid={`scope-po-dropzone-${it.boq_id}`} />
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">{isVar ? "Total Qty" : "Approved Qty"}</label>
                  <Input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1"
                    data-testid={`scope-po-qty-${it.boq_id}`} />
                </div>
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">Rate (₹)</label>
                  <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1"
                    data-testid={`scope-po-rate-${it.boq_id}`} />
                </div>
                <div className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-md p-3 flex justify-between items-center"
                  data-testid={`scope-po-amount-${it.boq_id}`}>
                  <span className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Amount</span>
                  <span className="text-lg font-semibold tracking-tight text-zinc-900">₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="PO reference #" className="max-w-[180px]"
                  data-testid={`scope-po-ref-${it.boq_id}`} />
                {isVar && (
                  <label className="flex items-center gap-1.5 text-xs text-violet-700">
                    <input type="checkbox" checked={amended} onChange={(e) => setAmended(e.target.checked)}
                      data-testid={`scope-po-amended-${it.boq_id}`} /> Amended PO
                  </label>
                )}
                <button onClick={() => submitPO("po")} disabled={saving || !url}
                  data-testid={`scope-po-submit-${it.boq_id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
                  <Upload size={15} /> {saving ? "Saving…" : "Upload PO"}
                </button>
                <button onClick={() => submitPO("email")} disabled={saving || !url}
                  data-testid={`scope-po-submit-email-${it.boq_id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                  <Mail size={15} /> Upload email confirmation
                </button>
              </div>
            </div>
          )}

          {/* Approve variation (po_uploaded) — or replace a wrong PO first */}
          {poUploaded && (
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={approveVar} disabled={saving} data-testid={`scope-po-approve-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 text-white px-4 py-2 text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                <CheckCircle2 size={15} /> Approve variation
              </button>
              <button onClick={() => setReplacing((r) => !r)} data-testid={`scope-po-replace-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium hover:bg-zinc-50">
                <Upload size={15} /> {replacing ? "Cancel" : "Replace PO"}
              </button>
              {!replacing && (
                <span className="text-xs text-zinc-400">Uploaded the wrong PO? Replace it before approving.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function BasePOCard({ it, code, onChanged }) {
  const [open, setOpen] = useState(true);
  const [url, setUrl] = useState("");
  const [poName, setPoName] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const lines = it.lines || [];
  const [selected, setSelected] = useState(() => new Set(lines.map((l) => l.boq_id)));

  // When the uncovered line set changes (e.g. after a partial PO upload refreshes
  // the board), reset selection + clear the form so labels never go stale.
  const lineKey = lines.map((l) => l.boq_id).join(",");
  useEffect(() => {
    setSelected(new Set(lines.map((l) => l.boq_id)));
    setUrl(""); setPoName(""); setPoNumber("");
  }, [lineKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selectedCount = lines.filter((l) => selected.has(l.boq_id)).length;
  const allOn = selectedCount === lines.length && lines.length > 0;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(lines.map((l) => l.boq_id)));

  async function upload(docType = "po") {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the PO file"); return; }
    const ids = lines.map((l) => l.boq_id).filter((id) => selected.has(id));
    if (ids.length === 0) { toast.error("Select at least one item this covers"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/line-pos`, {
        file_url: url, file_name: poName, po_number: poNumber,
        boq_ids: ids, doc_type: docType,
      });
      toast.success(docType === "email"
        ? `Email confirmation saved for ${ids.length} item(s) — awaiting the actual PO.`
        : `Base PO uploaded — ${ids.length} scope line(s) covered.`);
      setUrl(""); setPoName("");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  const anyEmail = lines.some((l) => l.email_confirmed);

  return (
    <div className="rounded-2xl border bg-white shadow-sm overflow-hidden border-l-4 border-l-rose-500 border-zinc-200"
      data-testid={`scope-po-card-base-${code}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`scope-po-toggle-base-${code}`}
        className="w-full px-5 py-3.5 flex items-center gap-3 text-left">
        <ChevronDown size={16} className={`text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 text-amber-600">
          <FileText size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 truncate">Base project PO</div>
          <div className="text-xs text-zinc-400 truncate">{it.description}</div>
        </div>
        <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${anyEmail ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}
          data-testid={`scope-po-status-base-${code}`}>{anyEmail ? "Email confirmed · PO pending" : "Needs PO"}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {it.total_count > 0 && (
            <div className="flex items-center gap-2" data-testid={`scope-po-base-coverage-${code}`}>
              <div className="h-1.5 flex-1 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${Math.round((it.covered_count / it.total_count) * 100)}%` }} />
              </div>
              <span className="text-xs font-mono text-zinc-500 tabular-nums shrink-0">
                {it.covered_count} of {it.total_count} base item(s) have a PO
              </span>
            </div>
          )}
          <p className="text-xs text-zinc-500">
            Tick the items this PO covers — one PO usually covers most base items. Anything left unticked stays open for another PO.
          </p>
          <div className="rounded-xl border border-zinc-200 overflow-hidden">
            <button onClick={toggleAll} type="button" data-testid={`scope-po-base-selectall-${code}`}
              className="w-full px-3 py-2 flex items-center gap-2.5 text-left bg-zinc-50 border-b border-zinc-200 hover:bg-zinc-100 transition-colors">
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${allOn ? "bg-zinc-900 border-zinc-900" : "border-zinc-300 bg-white"}`}>
                {allOn && <CheckCircle2 size={11} className="text-white" />}
              </span>
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                {allOn ? "All items" : `${selectedCount} of ${lines.length} selected`}
              </span>
            </button>
            <div className="divide-y divide-zinc-100">
              {lines.map((l) => {
                const on = selected.has(l.boq_id);
                return (
                  <button key={l.boq_id} type="button" onClick={() => toggle(l.boq_id)}
                    data-testid={`scope-po-base-line-${l.boq_id}`}
                    className="w-full px-3 py-2.5 flex items-center gap-2.5 text-left hover:bg-zinc-50 transition-colors">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-zinc-900 border-zinc-900" : "border-zinc-300 bg-white"}`}>
                      {on && <CheckCircle2 size={11} className="text-white" />}
                    </span>
                    <span className={`flex-1 text-sm ${on ? "text-zinc-800" : "text-zinc-400"}`}>{l.material_name}</span>
                    {l.email_confirmed && (
                      <a href={l.email_confirm_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        data-testid={`scope-po-base-email-${l.boq_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
                        <Mail size={10} /> Email confirmed
                      </a>
                    )}
                    <span className="text-xs text-zinc-400 font-mono">{l.quantity_required || ""} {l.unit}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 space-y-4">
            <FileUpload value={url} label="Attach base PO / email confirmation"
              onChange={(x) => { setUrl(x); setPoName(x ? (x.split("/").pop() || "po") : ""); }}
              testid={`scope-po-base-dropzone-${code}`} />
            <div className="flex flex-wrap items-center gap-3">
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO number" className="max-w-[200px]"
                data-testid={`scope-po-base-number-${code}`} />
              <button onClick={() => upload("po")} disabled={saving || !url || selectedCount === 0} data-testid={`scope-po-base-submit-${code}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
                <Upload size={15} /> {saving ? "Saving…" : `Upload PO · ${selectedCount} item(s)`}
              </button>
              <button onClick={() => upload("email")} disabled={saving || !url || selectedCount === 0} data-testid={`scope-po-base-submit-email-${code}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                <Mail size={15} /> Upload email confirmation
              </button>
            </div>
            <p className="text-[11px] text-zinc-400">
              No PO yet? Upload the email confirmation to mark these lines "awaiting PO" — then upload the actual PO once it arrives to close them.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
